import React, { useMemo, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Panel } from 'primereact/panel';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useGetPreApprovalCommentsQuery } from '../../../../../api/endpoints/preApproval'; 
import { formatDateOnly } from '../../../../../utils/dateUtils';

const PreApprovalCommentsTable = ({ preApprovalId, dialogVisible }) => {
    const { th } = useLanguage();
    const [collapsed, setCollapsed] = useState(true);
    const panelRef = useRef(null);

    const shouldFetch = !!preApprovalId && !!dialogVisible;

    const { data, isLoading, isFetching, isError } = useGetPreApprovalCommentsQuery(
        { preApprovalId },
        { skip: !shouldFetch }
    );

    const items = useMemo(() => data?.items || [], [data]);

    // dialog açıldı ama hiç yorum yoksa UI'ı göstermeyelim (senin eski davranışın)
    if (!isLoading && !isFetching && items.length === 0) return null;

    const commentDateBody = (row) => {
        const dt = row?.commentDate ? new Date(row.commentDate) : null;
        if (!dt || isNaN(dt)) return '-';
        return formatDateOnly(dt);
    };

    const typeNameBody = (row) => {
        if (!row?.typeName) return '-';

        // Eğer PreApproval için ayrı namespace açacaksan:
        const key = `warrantyClaims.detail.comments.types.${row.typeName}`;
        const translated = th(key);
        return translated === key ? row.typeName : translated;
    };

    const typeBody = (row) => {
        const dealerName = row?.dealerName;
        switch (row?.typeId) {
            case 1:
                return dealerName ? `${dealerName} (${th('warrantyClaims.detail.comments.types.WorkShop')})` : th('warrantyClaims.detail.comments.types.WorkShop');
            case 2:
                return dealerName ? `${dealerName} (${th('warrantyClaims.detail.comments.types.Dealer')})` : th('warrantyClaims.detail.comments.types.Dealer');
            case 3:
                return th('warrantyClaims.detail.comments.types.KarsanWarrantyTeam');
            default:
                return typeNameBody(row);
        }
    };

    const togglePanel = () => {
        const togglerBtn = panelRef.current?.getElement?.()?.querySelector('.p-panel-header-icon');
        if (togglerBtn) togglerBtn.click();
        else setCollapsed((prev) => !prev);
    };

    const headerTemplate = () => (
        <div className="flex align-items-center justify-content-between w-full">
            <div className="flex align-items-center">
                <i className="pi pi-comments mr-2" style={{ fontSize: '0.9rem' }} />
                <span className="font-semibold">{th('warrantyClaims.detail.comments.title') || 'Comments'}</span>
                <span className="ml-2 text-sm text-gray-600">({items.length})</span>
            </div>
        </div>
    );

    return (
        <div className="col-12 mt-3">
            <Panel
                ref={panelRef}
                header={headerTemplate}
                toggleable
                collapsed={collapsed}
                onToggle={(e) => setCollapsed(e.value)}
                expandIcon="pi pi-chevron-down"
                collapseIcon="pi pi-chevron-up"
                pt={{
                    header: {
                        onClick: togglePanel,
                        style: { cursor: 'pointer', boxShadow: 'none' },
                        tabIndex: 0,
                        onKeyDown: (e) => {
                            if (e.key === 'Enter' || e.key === ' ') togglePanel();
                        }
                    },
                    content: { style: { padding: 0 } }
                }}
            >
                <DataTable
                    value={items}
                    loading={isLoading || isFetching}
                    size="small"
                    stripedRows
                    showGridlines
                    responsiveLayout="scroll"
                    scrollable
                    scrollHeight="220px"
                    className="w-full"
                    emptyMessage={th('common.noRecords') || ''}
                    pt={{
                        wrapper: { style: { border: 'none' } },
                        table: { style: { border: 'none' } }
                    }}
                >
                    <Column header={th('warrantyClaims.detail.comments.date') || 'Date'} body={commentDateBody} style={{ width: '140px' }} />
                    <Column field="commentedBy" header={th('warrantyClaims.detail.comments.commentedBy') || 'Commented By'} style={{ width: '220px' }} />
                    <Column header={th('warrantyClaims.detail.comments.type') || 'Type'} body={typeBody} style={{ width: '180px' }} />
                    <Column field="comment" header={th('warrantyClaims.detail.comments.comment') || 'Comment'} />
                </DataTable>

                {isError && <small className="p-error">{th('common.error') || 'Error'}</small>}
            </Panel>
        </div>
    );
};

export default PreApprovalCommentsTable;
